




































































































































































































import { Vue, Component, Ref, Prop, PropSync, Watch } from "vue-property-decorator";
import store from "@/store";
import moment from "moment";
//  @types
import { Header, Row } from "@/types/VDataTable";
import { VesselAdmin } from "@/types/vesselAdmin";

//  @components
import { headers } from "@/components/Settings/ManageVessels/headers";
import DataCell from "./DataCell.vue";
import SortableArrows from "@/components/SortableArrows.vue";
import Tooltip from "@/components/Tooltip.vue";
import VesselDataModal from "./VesselDataModal.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
//  @modules
import { getModule } from "vuex-module-decorators";
import VesselsAdminModule from "@/store/clients/VesselsAdmin.module";
import SnackbarModule from "@/store/clients/Snackbar.module";
import { ExtendedVessel } from "@/types/vessel";
import vesselsAdministrationsClient from "@/Scripts/clients/vessels-administrations-client";

const VesselsAdmin = getModule(VesselsAdminModule, store);
const Snackbar = getModule(SnackbarModule, store);

@Component({
  components: {
    DataCell,
    SortableArrows,
    Tooltip,
    VesselDataModal,
    ConfirmDialog,
  },
})
export default class DataTable extends Vue {
  @Ref("confirmDelete") confirmDelete!: any;
  //  @Data
  VesselDataModal = false;
  loading = false;
  isLoadingTextDisplay = false;
  sortDesc = false;
  searchQuery = "";
  headers: Header[] = headers;
  sortBy = "";
  pageLimit = 20; // -1 show all items by default
  pageCount = 0;
  currentItems: Row[] = [];
  currentPage = 1;
  vesselsAdminList: VesselAdmin[] = [];
  selectedFilter: "All" | "Unassigned" | "Assigned" | "Archived" = "Unassigned";
  vesselToEditId: number | null = null;

  // @Watchers
  updateSortBy(value: string): void {
    if (!value) value = "";
  }

  updateSortDesc(value: boolean): void {
    if (!value) value = false;
  }

  updateItemsPerPage(value: number): void {
    if (!value) value = 20;
    this.pageLimit = value;
  }

  //  @Getters
  get rows(): any {
    let rows = this.vesselsAdminList.map((v, index) => {
      const activeStatus = (v as any).activeStatus ?? (v as any).status ?? (v.companyName ? "Assigned" : "Unassigned");

      return {
        index: index,
        id: {
          value: v.id,
        },
        vesselName: {
          value: v.name,
          vesselId: v.id,
          subtitle: `IMO${v.imoNumber}`,
        },
        lastDataSent: {
          value: v.lastExportDate ? moment.utc(v.lastExportDate).format("DD. MMM, YYYY") : "",
          subtitle: v.lastExportDate ? moment.utc(v.lastExportDate).fromNow() : "",
        },
        created: {
          value: v.created ? moment.utc(v.created).format("DD. MMM, YYYY") : "",
          subtitle: v.created ? moment.utc(v.created).fromNow() : "",
        },
        assignToCompany: {
          value: v.companyName ?? "Unassigned",
        },
        actions: {
          value: this.actionsListByStatus(activeStatus),
        },
        // keep for filtering/sorting
        activeStatus,
        imoNumber: v.imoNumber,
      };
    });
    // Filter by search query
    if (this.searchQuery && this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      rows = rows.filter(item => {
        // Search by vessel name or IMO number
        const nameMatch = item.vesselName.value.toLowerCase().includes(query);
        const imoMatch = item.imoNumber ? String(item.imoNumber).includes(query) : false;
        return nameMatch || imoMatch;
      });
    }

    // Apply selected filter (default Unassigned)
    if (this.selectedFilter && this.selectedFilter !== "All") {
      rows = rows.filter(item => {
        if (this.selectedFilter === "Unassigned") {
          return item.activeStatus === "Unassigned" || item.assignToCompany.value === "Unassigned";
        }
        if (this.selectedFilter === "Assigned") {
          return item.activeStatus === "Assigned" || item.activeStatus === "Active";
        }
        if (this.selectedFilter === "Archived") {
          return item.activeStatus === "Archived";
        }
        return true;
      });
    }

    return rows;
  }

  get isLessThanLimit(): boolean {
    return this.rows.length <= this.pageLimit;
  }

  get skeletonStructure(): object {
    return { "table-row": `table-cell@6` };
  }

  get filteredHeaders(): Header[] {
    const result = this.headers.filter(header => {
      return true;
    });
    return result;
  }

  get sortedRows() {
    if (!this.sortBy) return this.rows;

    return this.rows.slice().sort((a: any, b: any) => {
      // Special handling for Last Data Sent and Created columns
      if (this.sortBy === "lastDataSent" || this.sortBy === "created") {
        const dateA = new Date(a[this.sortBy]?.value ?? "");
        const dateB = new Date(b[this.sortBy]?.value ?? "");
        if (this.sortDesc) {
          return dateB.getTime() - dateA.getTime();
        } else {
          return dateA.getTime() - dateB.getTime();
        }
      }

      // Default string/other sorting
      const valueA = a[this.sortBy]?.value ?? "";
      const valueB = b[this.sortBy]?.value ?? "";

      // Normalize values for comparison
      const normalizedA = typeof valueA === "string" ? valueA.toLowerCase() : valueA;
      const normalizedB = typeof valueB === "string" ? valueB.toLowerCase() : valueB;

      if (this.sortDesc) {
        return normalizedA < normalizedB ? 1 : normalizedA > normalizedB ? -1 : 0;
      } else {
        return normalizedA > normalizedB ? 1 : normalizedA < normalizedB ? -1 : 0;
      }
    });
  }

  //  @Methods
  cellData(row: Row, header: Header, index: number): any {
    return {
      column: header.value,
      columnName: header.text,
      vesselId: row.vesselName.vesselId,
      vesselName: row.vesselName.value,
      imo: row.vesselName.subtitle ? Number(row.vesselName.subtitle.replace(/\D/g, "")) : null,
      type: header.type,
      align: header.align,
      value: row[header.value].value,
      subtitle: row[header.value].subtitle,
      link: row[header.value].link,
    };
  }

  // return available actions list depending on activeStatus
  actionsListByStatus(activeStatus: string): string[] {
    switch (activeStatus) {
      case "Unassigned":
        return ["edit"];
      case "Archived":
        return ["edit", "activate", "delete"];
      case "Assigned":
        return ["edit", "archive", "activate"];
      case "Active":
        return ["edit", "archive", "deactivate"];
      default:
        return ["edit", "archive", "delete"];
    }
  }

  // set filter when clicking filter buttons
  setFilter(filter: "All" | "Unassigned" | "Assigned" | "Archived"): void {
    this.selectedFilter = filter;
    // reset to first page
    this.currentPage = 1;
  }

  async onSubmitVesselData(payload: { vesselData: ExtendedVessel; assigned: boolean; isNewVessel: boolean }): Promise<void> {
    this.setFilter(payload.assigned ? "Assigned" : "Unassigned");
    if (payload.isNewVessel) this.searchQuery = payload.vesselData.name;
    try {
      this.loading = true;
      this.isLoadingTextDisplay = true;
      await VesselsAdmin.refreshAllVessels();
      await this.initializeTable();
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to load vessels data after adding/editing vessel" });
    } finally {
      this.loading = false;
      this.isLoadingTextDisplay = false;
    }
  }

  headerClasses(header: any): string[] {
    return [
      header.class,
      {
        sortable: header.sortable,
        "no-sort": this.sortBy !== header.value,
      },
    ];
  }

  headerStyles(header: any): any {
    return {
      width: header.width ? header.width : "auto",
      "min-width": header.width ? header.width : "auto",
    };
  }

  addVessel(): void {
    this.vesselToEditId = null;
    this.VesselDataModal = true;
    this.$emit("add-vessel");
  }

  onEditVessel(vesselId: number): void {
    this.vesselToEditId = vesselId;
    this.VesselDataModal = true;
    this.$emit("edit-vessel", vesselId);
  }

  async onArchiveEvent(vesselId: number): Promise<void> {
    try {
      await vesselsAdministrationsClient.archiveVessel(vesselId);
      await VesselsAdmin.refreshAllVessels();
      await this.initializeTable();
      Snackbar.showSnackbar({ text: `Vessel ${this.vesselsAdminList.find(v => v.id === vesselId)?.name} has been archived`, color: "success" });
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to archive vessel" });
    }
    this.$emit("archive-vessel", vesselId);
  }

  async onActivateEvent(vesselId: number): Promise<void> {
    try {
      await vesselsAdministrationsClient.activateVessel(vesselId);
      await VesselsAdmin.refreshAllVessels();
      await this.initializeTable();
      Snackbar.showSnackbar({ text: `Vessel ${this.vesselsAdminList.find(v => v.id === vesselId)?.name} has been activated`, color: "success" });
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to activate vessel" });
    }
    this.$emit("activate-vessel", vesselId);
  }

  async onDeactivateEvent(vesselId: number): Promise<void> {
    try {
      await vesselsAdministrationsClient.deactivateVessel(vesselId);
      await VesselsAdmin.refreshAllVessels();
      await this.initializeTable();
      Snackbar.showSnackbar({ text: `Vessel ${this.vesselsAdminList.find(v => v.id === vesselId)?.name} has been deactivated`, color: "success" });
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to deactivate vessel" });
    }
    this.$emit("deactivate-vessel", vesselId);
  }

  async onDeleteEvent(vesselId: number): Promise<void> {
    const vesselName = this.vesselsAdminList.find(v => v.id === vesselId)?.name || "";
    const confirmed = await this.confirmDelete.open("Delete vessel?", `Are you sure you want to delete '${vesselName}' vessel?`);
    if (!confirmed) return;
    try {
      await vesselsAdministrationsClient.deleteVessel(vesselId);
      await VesselsAdmin.refreshAllVessels();
      await this.initializeTable();
      Snackbar.showSnackbar({ text: `Vessel ${vesselName} has been deleted`, color: "success" });
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to delete vessel" });
    }
    this.$emit("delete-vessel", vesselId);
  }

  async onAssignCompany(payload: { vesselId: number; companyId: number }): Promise<void> {
    const vesselName = this.vesselsAdminList.find(v => v.id === payload.vesselId)?.name || "";
    try {
      await vesselsAdministrationsClient.assign(payload.vesselId, payload.companyId);
      await VesselsAdmin.refreshAllVessels();
      await this.initializeTable();
      Snackbar.showSnackbar({ text: `Company has been assigned to ${vesselName}`, color: "success" });
    } catch (error) {
      Snackbar.showSnackbar({ text: `Failed to assign company to ${vesselName}` });
    }
    this.$emit("assign-company", payload);
  }

  async initializeTable(): Promise<void> {
    try {
      this.loading = true;
      this.isLoadingTextDisplay = false;
      this.vesselsAdminList = await VesselsAdmin.getAll();

      // Show loading text if data takes more than 2 seconds to load
      setTimeout(() => {
        if (this.loading) {
          this.isLoadingTextDisplay = true;
        }
      }, 2000);
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to load vessels data" });
    } finally {
      this.loading = false;
      this.isLoadingTextDisplay = false;
    }
  }

  sort(value: string): void {
    if (this.sortBy === value) {
      this.sortDesc = !this.sortDesc; // Toggle sort direction
      if (!this.sortDesc) this.sortBy = ""; // Reset sorting if toggled off
    } else {
      this.sortBy = value;
      this.sortDesc = false; // Default to ascending order
    }
  }

  clearSearch(): void {
    this.searchQuery = "";
  }

  //  @Hooks
  async created(): Promise<void> {
    await this.initializeTable();
  }
}
